import { axiosInstance } from "../axiosInstance";
import {
  Partner,
  PartnerFilters,
  CreatePartnerRequest,
  UpdatePartnerRequest,
  FetchPartnersResponse,
  PartnerResponse,
  PartnerStatisticsResponse,
} from "./types";
import {
  Transaction,
  TransactionFilters,
  CreateTransactionRequest,
  UpdateTransactionRequest,
} from "../partnerTransactions/types";

export const partnersApi = {
  // Fetch partners with optional filtering and pagination
  fetchPartners: async (filters?: PartnerFilters): Promise<FetchPartnersResponse> => {
    const response = await axiosInstance.get<FetchPartnersResponse>("/api/partners", {
      params: filters,
    });
    return response.data;
  },

  // Fetch single partner by ID
  fetchPartner: async (id: string): Promise<Partner> => {
    const response = await axiosInstance.get<PartnerResponse>(`/api/partners/${id}`);
    return response.data.data;
  },

  // Create new partner
  createPartner: async (data: CreatePartnerRequest): Promise<Partner> => {
    const response = await axiosInstance.post<PartnerResponse>("/api/partners", data);
    return response.data.data;
  },

  // Update existing partner
  updatePartner: async (id: string, data: UpdatePartnerRequest): Promise<Partner> => {
    const response = await axiosInstance.put<PartnerResponse>(`/api/partners/${id}`, data);
    return response.data.data;
  },

  // Partial update partner
  patchPartner: async (id: string, data: Partial<UpdatePartnerRequest>): Promise<Partner> => {
    const response = await axiosInstance.patch<PartnerResponse>(`/api/partners/${id}`, data);
    return response.data.data;
  },

  // Delete partner
  deletePartner: async (id: string): Promise<void> => {
    await axiosInstance.delete(`/api/partners/${id}`);
  },

  // Activate partner
  activatePartner: async (id: string): Promise<{ message: string }> => {
    const response = await axiosInstance.post<{ message: string }>(`/api/partners/${id}/activate`);
    return response.data;
  },

  // Deactivate partner
  deactivatePartner: async (id: string): Promise<{ message: string }> => {
    const response = await axiosInstance.post<{ message: string }>(`/api/partners/${id}/deactivate`);
    return response.data;
  },

  // Get partner statistics
  getPartnerStatistics: async (id: string): Promise<PartnerStatisticsResponse> => {
    const response = await axiosInstance.get<PartnerStatisticsResponse>(`/api/partners/${id}/statistics`);
    return response.data;
  },

  // Search partners
  searchPartners: async (searchTerm: string): Promise<Partner[]> => {
    const response = await axiosInstance.get<FetchPartnersResponse>("/api/partners", {
      params: { search: searchTerm },
    });
    return response.data.data;
  },

  // Get partners by country
  getPartnersByCountry: async (country: string): Promise<Partner[]> => {
    const response = await axiosInstance.get<FetchPartnersResponse>("/api/partners", {
      params: { country },
    });
    return response.data.data;
  },

  // Get partners by category
  getPartnersByCategory: async (category: string): Promise<Partner[]> => {
    const response = await axiosInstance.get<FetchPartnersResponse>("/api/partners", {
      params: { category },
    });
    return response.data.data;
  },

  // Get active partners
  getActivePartners: async (): Promise<Partner[]> => {
    const response = await axiosInstance.get<FetchPartnersResponse>("/api/partners", {
      params: { active: true },
    });
    return response.data.data;
  },

  // Transaction Management APIs
  // Fetch transactions for a partner with optional filtering
  fetchTransactions: async (partnerId: string, filters?: TransactionFilters): Promise<{
    data: Transaction[];
    page: number;
    limit: number;
    total: number;
  }> => {
    const response = await axiosInstance.get(`/api/partners/${partnerId}/transactions`, {
      params: filters,
    });
    return response.data;
  },

  // Fetch single transaction by ID
  fetchTransaction: async (transactionId: string): Promise<Transaction> => {
    const response = await axiosInstance.get(`/api/transactions/${transactionId}`);
    return response.data.data;
  },

  // Create new transaction
  createTransaction: async (data: CreateTransactionRequest): Promise<Transaction> => {
    const response = await axiosInstance.post("/api/transactions", data);
    return response.data.data;
  },

  // Update existing transaction
  updateTransaction: async (id: string, data: UpdateTransactionRequest): Promise<Transaction> => {
    const response = await axiosInstance.put(`/api/transactions/${id}`, data);
    return response.data.data;
  },

  // Delete transaction
  deleteTransaction: async (id: string): Promise<void> => {
    await axiosInstance.delete(`/api/transactions/${id}`);
  },

  // Export transactions as CSV
  exportTransactions: async (partnerId: string, filters?: TransactionFilters): Promise<Blob> => {
    const response = await axiosInstance.get(`/api/partners/${partnerId}/transactions/export`, {
      params: filters,
      responseType: 'blob',
    });
    return response.data;
  },

  // Get transaction types
  getTransactionTypes: async (): Promise<string[]> => {
    const response = await axiosInstance.get("/api/transactions/types");
    return response.data.data;
  },

  // Get transaction statuses
  getTransactionStatuses: async (): Promise<string[]> => {
    const response = await axiosInstance.get("/api/transactions/statuses");
    return response.data.data;
  },
};